import User from '../data/models/user';
import { getActiveCompany, isScreenEnabled } from '../company/session';
import { BASE_ROUTES } from '../company/routeTable';

/**
 * Central access-control model.
 *
 * Permissions are editable by admins in the "Permisos" console: the service sends the current
 * user's role permissions ({ screens, actions }) with the login / token responses, stored on
 * `User.getInstance().permissions`. The maps below are the FALLBACK used when they're missing
 * (e.g. an older service); they mirror the service defaults in service/utils/permissions.js.
 * admin always has everything; the "permissions" screen is admin-only; the client role keeps
 * its fixed bespoke menu.
 *
 * Single source of truth for which nav areas each user type can reach. Both the
 * sidebar/command palette (what's shown) and the route guards (what's reachable)
 * derive from this map, so granting or revoking a role's access to a section is a
 * one-line change here instead of edits scattered across three files.
 *
 * Keys correspond to sidebar nav keys / route nav-keys. `'all'` grants everything,
 * `{ deny: [...] }` grants everything except the listed keys, and an array is an
 * explicit allow-list (any key not listed is denied). admin/gerente see the full
 * app; asesor and client are explicit allow-lists.
 *
 * On top of the role, the signed-in company's navigation decides which screens exist at all
 * (`isScreenEnabled`, company/session.js): a screen outside it is denied to every role. A
 * company may also bring its own fallback maps and console rule (`access` in
 * companies/<id>/company.js); Mary uses the maps below.
 */

/** The "Precio" nav group — the only area Trade Marketing users may not reach. */
export const PRICE_KEYS = ['price', 'distributor-prices', 'levantamiento-general'];

export const ROLE_ACCESS = {
    admin: 'all',
    gerente: 'all',
    // Trade Marketing: full use of Shelf EXCEPT anything price-related. Expressed as a
    // deny-list on purpose — with an allow-list, every new report/section had to be added
    // here or it silently became unreachable for these users.
    TradeMarketing: { deny: PRICE_KEYS },
    // Asesor: prices, all reports and analytics, but only the Promotores / Contrataciones /
    // Campañas slice of Trade Marketing (no auditors, audit console, product approval or polls).
    // Everything they see is additionally scoped server-side to the clients they handle
    // (Client.asesor) — see service/utils/asesorScope.js.
    asesor: [
        'dashboard',
        'clients',
        'stores',
        'price',
        'distributor-prices',
        'levantamiento-general',
        'sos-analysis',
        'user-interactions',
        'share-of-shelf',
        'numeric-distribution',
        'uso-de-shelf',
        'route-efficiency',
        'activity-logs',
        'promoters',
        'promoters-analytics',
        'contracts',
        'campaigns',
    ],
    client: [
        'my-client',
        'stores',
        'promoters',
    ],
    // Análisis Auditor: the auditor slice of Trade Marketing and nothing else — the Auditores
    // list (where the only edit allowed is an auditor's pisos) and the audit console limited to
    // the "Visitas por tienda" / "Registros (escaneos)" tabs with the auditor filter pinned.
    // Both restrictions are also enforced server-side (audit.controller.js _requireAuditViewer).
    auditAnalyst: [
        'auditors',
        'audit-console',
    ],
};

/** Admin-only screen where permissions are edited. */
export const PERMISSIONS_KEY = 'permissions';

/** Fallback action permissions per role (see the service's EDITABLE_ROLES / FIXED_ROLES). */
const ALL_ACTIONS = [
    'prices.view',
    'data.export',
    'clients.create',
    'users.manage-all-roles',
    'audit.manage',
    'auditors.remove',
    'store-overrides.manage',
];
export const ROLE_ACTIONS = {
    gerente: ALL_ACTIONS.filter((a) => a !== 'store-overrides.manage'),
    TradeMarketing: ['data.export', 'clients.create', 'audit.manage', 'auditors.remove'],
    asesor: ['prices.view', 'data.export', 'users.manage-all-roles', 'auditors.remove'],
    auditAnalyst: ['prices.view', 'data.export', 'users.manage-all-roles'],
    client: ['prices.view', 'data.export'],
};

/** The signed-in company's access overrides (empty for the base company). */
function companyAccess() {
    return getActiveCompany().access || {};
}

/** The server-provided permissions of the logged-in user, when they belong to `userType`. */
function loadedPermissions(userType) {
    const user = User.getInstance();
    if (user?.userType !== userType || !user?.permissions) return null;
    return user.permissions;
}

/**
 * Whether a user type may perform an action (functionality inside screens), e.g.
 * 'prices.view', 'data.export', 'store-overrides.manage'.
 */
export function can(userType, action) {
    if (userType === 'admin') return true;
    const loaded = loadedPermissions(userType);
    if (loaded && userType !== 'client') return (loaded.actions || []).includes(action);
    const fallback = companyAccess().roleActions || ROLE_ACTIONS;
    return (fallback[userType] || []).includes(action);
}

/** Whether a user type is the read-mostly Análisis Auditor role. */
export function isAuditAnalyst(userType) {
    return userType === 'auditAnalyst';
}

/**
 * Where a user lands after login / when denied a route. Clients go to their own company
 * profile, Análisis Auditor to the Auditores list (they can't see the dashboard), everyone
 * else to the dashboard.
 */
export function homePathFor(user) {
    if (user?.userType === 'client') {
        return user?.client ? `/dashboard/clients/client?id=${user.client}` : '/dashboard/stores';
    }
    if (isAuditAnalyst(user?.userType)) return '/dashboard/trade/auditors';
    // Companies that opt in send a user without the dashboard to the first screen they may open.
    if (user && companyAccess().homeToFirstScreen && !canAccess(user.userType, 'dashboard')) {
        const first = BASE_ROUTES.find((route) => typeof route.screen === 'string' && route.screen !== 'dashboard' && canAccess(user.userType, route.screen));
        if (first) return first.path;
    }
    return '/dashboard';
}

/** Fallback for unknown/undefined user types: the dashboard and nothing else. */
const MINIMAL_ACCESS = ['dashboard'];

/**
 * Returns the access list for a user type. Unknown/undefined types fall back to a
 * minimal list so a misconfigured user never inherits another role's reach.
 */
export function accessListFor(userType) {
    if (userType === 'admin') return 'all';
    const fallback = companyAccess().roleScreens || ROLE_ACCESS;
    return fallback[userType] ?? MINIMAL_ACCESS;
}

/**
 * Whether a user type may see price information anywhere in the app. Trade Marketing users
 * fully use Shelf but have NO access to prices (levantamiento, price pills, price columns, …).
 */
export function canSeePrices(userType) {
    return can(userType, 'prices.view');
}

/**
 * Whether a user type may create clients and stores (PDVs). Trade Marketing users manage
 * their own catalog, so they create alongside admin/gerente.
 */
export function canCreateClientsAndStores(userType) {
    return can(userType, 'clients.create');
}

/**
 * Whether a user type may access a given nav key.
 */
export function canAccess(userType, key) {
    // A screen the signed-in company does not have is closed to every role.
    if (!isScreenEnabled(key)) return false;
    if (key === PERMISSIONS_KEY) {
        // Admin-only, unless the company lets an action open the console.
        const action = companyAccess().permissionsAction;
        return userType === 'admin' || Boolean(action && can(userType, action));
    }
    if (userType === 'admin') return true;
    const loaded = loadedPermissions(userType);
    if (loaded && userType !== 'client') return (loaded.screens || []).includes(key);
    const list = accessListFor(userType);
    if (list === 'all') return true;
    if (Array.isArray(list)) return list.includes(key);
    if (list?.deny) return !list.deny.includes(key);
    return false;
}

/**
 * Filters a (possibly nested) Ant Design menu tree down to what `userType` can
 * access. A parent group is kept when at least one of its children survives, so
 * parent group keys (e.g. 'precio') don't need to be listed in ROLE_ACCESS.
 */
export function filterMenuByAccess(items, userType) {
    return items
        .map((item) => {
            if (item.children) {
                const children = filterMenuByAccess(item.children, userType);
                if (children.length === 0) return null;
                return { ...item, children };
            }
            return canAccess(userType, item.key) ? item : null;
        })
        .filter(Boolean);
}
